import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "../lib/LangContext";
import { TID } from "../lib/testIds";

export default function Header() {
  const { t, lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navItems = [
    { id: "home", label: t.nav.home, testId: TID.navHome, href: onLanding ? "#home" : "/#home" },
    { id: "services", label: t.nav.services, testId: TID.navServices, href: onLanding ? "#services" : "/#services" },
    { id: "quote", label: t.nav.quote, testId: TID.navQuote, href: onLanding ? "#quote" : "/#quote" },
    { id: "contact", label: t.nav.contact, testId: TID.navContact, href: onLanding ? "#contact" : "/#contact" },
  ];

  const handleNavClick = (e, href) => {
    if (href.startsWith("#") && onLanding) {
      e.preventDefault();
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top, behavior: "smooth" });
      }
      setOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
          : "bg-black/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group" data-testid={TID.headerLogo}>
            <div className="relative">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D4AF37] flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                <span className="font-display text-black text-xl sm:text-2xl font-bold leading-none">Z</span>
              </div>
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#E3000F]">
                <span className="absolute left-1/2 -translate-x-1/2 top-[2px] w-[1.5px] h-[8px] bg-white" />
                <span className="absolute top-1/2 -translate-y-1/2 left-[2px] h-[1.5px] w-[8px] bg-white" />
              </span>
            </div>
            <span className="flex flex-col leading-tight">
              <span className="font-display text-white text-base sm:text-lg tracking-wide">ZEMI SWISS</span>
              <span className="hidden sm:block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Transport &amp; Reinigung</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                data-testid={item.testId}
                className="gold-link text-sm font-medium text-white/90 hover:text-white transition-colors"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-4">
            <div
              className="hidden sm:flex items-center gap-1 border border-white/15 rounded-none"
              data-testid={TID.langSwitcher}
              role="group"
              aria-label="Language switcher"
            >
              {languages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setLang(l.code)}
                  data-testid={TID.langOption(l.code)}
                  className={`px-2.5 py-1 text-xs font-semibold tracking-widest transition-colors ${
                    lang === l.code
                      ? "bg-[#D4AF37] text-black"
                      : "text-white/70 hover:text-white"
                  }`}
                  aria-pressed={lang === l.code}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <a
              href="tel:+41783215249"
              data-testid={TID.callTopButton}
              className="hidden md:inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] text-black px-4 py-2 text-sm font-semibold transition-colors"
            >
              <Phone className="w-4 h-4" strokeWidth={2} />
              <span>+41 78 321 52 49</span>
            </a>

            <button
              type="button"
              className="lg:hidden p-2 text-white"
              aria-label="Menu"
              data-testid={TID.mobileMenuToggle}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          data-testid={TID.mobileMenu}
        >
          <div className="max-w-7xl mx-auto px-4 py-6 space-y-1">
            {navItems.map((item) => (
              <a
                key={item.id}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="block py-3 text-white border-b border-white/10 text-base"
                data-testid={`mobile-${item.testId}`}
              >
                {item.label}
              </a>
            ))}
            <div className="pt-4 flex items-center gap-2">
              {languages.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => setLang(l.code)}
                  data-testid={`mobile-${TID.langOption(l.code)}`}
                  className={`px-3 py-2 text-xs font-semibold tracking-widest transition-colors border border-white/20 ${
                    lang === l.code ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "text-white"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <a
              href="tel:+41783215249"
              className="mt-4 inline-flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-3 text-sm font-semibold w-full justify-center"
              data-testid="mobile-call-button"
            >
              <Phone className="w-4 h-4" /> +41 78 321 52 49
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
